import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const Ext = ({ href, children }) => (
  <a href={href} className="text-[#FF6A5E] hover:underline" target="_blank" rel="noreferrer">{children}</a>
);

const SECTIONS = [
  {
    n: "1", title: "Acceptance of Terms",
    body: ["By accessing or using Faceless Studio, you agree to be bound by these Terms of Service and all terms incorporated by reference, including our Privacy Policy.", "If you do not agree to these Terms, you may not access or use the Services.", "If you use the Services on behalf of an organization, you represent that you have authority to bind that organization to these Terms."],
  },
  {
    n: "2", title: "Eligibility",
    body: ["You must be at least the age of majority in your jurisdiction, or the minimum age required to use the Services under applicable law, to create an account or use the Services.", "By using Faceless Studio, you represent and warrant that you meet these requirements and that all registration information you provide is accurate and complete."],
  },
  {
    n: "3", title: "Accounts and Security",
    intro: "When you create an account, you agree to:",
    list: ["Provide accurate, current, and complete information", "Maintain and promptly update your account information", "Keep your login credentials confidential", "Be responsible for all activity that occurs under your account", "Notify us immediately of any unauthorized use or security breach"],
    outro: "We may suspend or terminate accounts that violate these Terms or that we reasonably believe are being used for fraudulent, abusive, or unlawful activity.",
  },
  {
    n: "4", title: "Description of the Services",
    body: ["Faceless Studio provides tools that help users create short-form advertising and marketing videos using artificial intelligence, including script generation, AI voiceovers, AI visuals, captions, and video compositing.", "The Services may also include subscription plans, credit packs, social-media integrations, publishing tools, and analytics.", "We may modify, add, or remove features of the Services at any time, with or without notice, subject to applicable law."],
  },
  {
    n: "5", title: "Subscriptions, Credits, and Payments",
    intro: "Certain features require a paid subscription or the purchase of credits. By purchasing a paid plan or credits, you agree that:",
    list: ["Payments are processed by third-party payment processors", "Subscription fees are billed on a recurring basis until cancelled", "You authorize us and our payment processors to charge your selected payment method", "Prices, plans, and usage limits may change with reasonable notice", "Credits and plan allowances are subject to the limits described at the time of purchase", "Except where required by law, fees are non-refundable"],
    outro: "You are responsible for all applicable taxes associated with your purchases.",
  },
  {
    n: "6", title: "Free Trial and Usage Limits",
    body: ["The Services may offer a free trial or limited-use tier subject to usage restrictions, such as a maximum number of videos per period.", "We may enforce these limits using reasonable methods, including by email address and IP address, and may modify or discontinue trial offerings at any time.", "Attempting to circumvent usage limits, quotas, or eligibility restrictions is a violation of these Terms."],
  },
  {
    n: "7", title: "User Content and License",
    body: ["You retain ownership of the content you upload, submit, or create through the Services (\"User Content\").", "By using the Services, you grant Faceless Studio a limited, non-exclusive, worldwide, royalty-free license to host, store, reproduce, process, modify as technically necessary, and transmit your User Content solely to provide, maintain, secure, and improve the Services you request.", "You are solely responsible for your User Content and represent that you have all rights, licenses, and permissions necessary to upload it and to generate videos from it."],
  },
  {
    n: "8", title: "AI-Generated Content",
    body: ["The Services use artificial intelligence and third-party providers to generate videos, images, audio, scripts, captions, and other outputs.", "AI-generated output may be inaccurate, may resemble other content, and is provided without warranty of originality, fitness, or non-infringement.", "You are responsible for reviewing generated content before use and for ensuring it complies with applicable laws, platform policies, and third-party rights.", "Availability and behavior of AI providers may change over time."],
  },
  {
    n: "9", title: "Acceptable Use",
    intro: "You agree not to use the Services to create, upload, publish, or distribute content or to engage in conduct that:",
    list: ["Violates any applicable law or regulation", "Infringes the intellectual property, privacy, or other rights of any person", "Is fraudulent, deceptive, defamatory, harassing, or hateful", "Contains malware or attempts to compromise the security of the Services", "Impersonates any person or misrepresents your affiliation", "Generates deceptive, misleading, or unlawful advertising", "Depicts or exploits minors, or contains prohibited or illegal material", "Attempts to reverse engineer, scrape, or overload the Services", "Circumvents usage limits, access controls, or eligibility restrictions"],
    outro: "We may remove content or suspend access for violations of this section.",
  },
  {
    n: "10", title: "Advertising and Affiliate Disclosures",
    body: ["You are solely responsible for ensuring that any advertising, endorsements, affiliate links, or promotional claims created with the Services comply with applicable laws, disclosure requirements, and platform rules.", "Faceless Studio is not responsible for your compliance with advertising, consumer-protection, or disclosure regulations."],
  },
  {
    n: "11", title: "Social-Media Integrations and Publishing",
    body: ["If you connect a third-party platform such as YouTube, Facebook, Instagram, or TikTok, you authorize Faceless Studio to access and use the permissions you grant to perform the functions you request, including publishing content on your behalf.", "Faceless Studio only posts to your connected accounts when you ask it to, either when you choose to publish a specific video or through an auto-posting schedule that you enable and can turn off at any time.", <>Faceless Studio uses YouTube API Services. By connecting a YouTube account you agree to be bound by the <Ext href="https://www.youtube.com/t/terms">YouTube Terms of Service</Ext>, and you acknowledge the <Ext href="https://policies.google.com/privacy">Google Privacy Policy</Ext>. Our use of information received from Google APIs adheres to the <Ext href="https://developers.google.com/terms/api-services-user-data-policy">Google API Services User Data Policy</Ext>, including the Limited Use requirements. You may revoke access at any time via <Ext href="https://myaccount.google.com/permissions">Google security settings</Ext>.</>, "Your use of connected platforms is also governed by those platforms' own terms and policies.", "You are responsible for ensuring your content complies with each platform's rules. We are not responsible for actions taken by third-party platforms, including content removal or account restrictions."],
  },
  {
    n: "12", title: "Intellectual Property",
    body: ["The Services, including software, design, branding, and all related intellectual property, are owned by Faceless Studio or its licensors and are protected by law.", "Except for the rights expressly granted to you, these Terms do not transfer any ownership or intellectual-property rights to you.", "You may not copy, modify, distribute, sell, or lease any part of the Services without our prior written consent."],
  },
  {
    n: "13", title: "Third-Party Services",
    body: ["The Services may rely on or link to third-party services, including AI providers, payment processors, hosting providers, and social-media platforms.", "We are not responsible for the availability, accuracy, content, or practices of third-party services, and your use of them may be subject to their own terms."],
  },
  {
    n: "14", title: "Disclaimer of Warranties",
    body: ["The Services are provided \"as is\" and \"as available\" without warranties of any kind, whether express, implied, or statutory, to the fullest extent permitted by law.", "We do not warrant that the Services will be uninterrupted, error-free, secure, or that generated content will meet your requirements or be free of infringement.", "You use the Services at your own risk."],
  },
  {
    n: "15", title: "Limitation of Liability",
    body: ["To the fullest extent permitted by law, Faceless Studio and its affiliates, officers, employees, and suppliers will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, data, goodwill, or business.", "Our total aggregate liability arising out of or relating to the Services will not exceed the amount you paid to us for the Services in the twelve months preceding the event giving rise to the claim, or one hundred dollars, whichever is greater."],
  },
  {
    n: "16", title: "Indemnification",
    body: ["You agree to indemnify and hold harmless Faceless Studio and its affiliates from any claims, damages, liabilities, and expenses (including reasonable legal fees) arising out of your User Content, your use of the Services, your published content, or your violation of these Terms or applicable law."],
  },
  {
    n: "17", title: "Termination",
    body: ["You may stop using the Services and delete your account at any time.", "We may suspend or terminate your access to the Services at any time if you violate these Terms, if required by law, or to protect the Services or other users.", "Upon termination, your right to use the Services will cease, and we may delete your account and associated content, subject to our Privacy Policy and applicable law. Sections that by their nature should survive termination will continue to apply."],
  },
  {
    n: "18", title: "Governing Law",
    body: ["These Terms are governed by the laws applicable in Canada and the province in which Faceless Studio operates, without regard to conflict-of-law principles.", "Any disputes will be subject to the jurisdiction of the courts located in that province, unless otherwise required by applicable law."],
  },
  {
    n: "19", title: "Changes to These Terms",
    body: ["We may update these Terms from time to time.", "When we make material changes, we may provide notice through the Services, by email, or by another appropriate method.", "The \"Last Updated\" date indicates when these Terms were most recently revised. Your continued use of the Services after changes take effect constitutes acceptance of the revised Terms."],
  },
];

const Bullets = ({ items }) => (
  <ul className="mt-3 grid gap-1.5">
    {items.map((it) => (
      <li key={it} className="flex gap-2.5 text-slate-400 leading-relaxed">
        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#FF453A]/70" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const PAGE_TITLE = "Faceless Studio Terms of Service";

export default function Terms() {
  useEffect(() => { const prev = document.title; document.title = PAGE_TITLE; return () => { document.title = prev; }; }, []);
  return (
    <div className="relative max-w-3xl mx-auto px-5 sm:px-8 py-14" data-testid="terms-page">
      <div className="pointer-events-none absolute -top-16 right-0 h-72 w-72 rounded-full bg-[#FF453A]/10 blur-[120px]" />

      <Link
        to="/"
        data-testid="terms-back-link"
        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to home
      </Link>

      <div className="mt-8">
        <span className="eyebrow">Legal</span>
        <h1 className="mt-2 font-display font-extrabold text-4xl sm:text-5xl tracking-tight">Faceless Studio Terms of Service</h1>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-500">
          <span>Effective Date: September 29, 2026</span>
          <span>Last Updated: October 1, 2026</span>
        </div>
      </div>

      <div className="mt-8 space-y-4 text-slate-400 leading-relaxed">
        <p>
          These Terms of Service ("Terms") govern your access to and use of Faceless Studio's website, applications,
          software, and services (collectively, the "Services"). Please read them carefully.
        </p>
        <p>
          These Terms form a binding agreement between you and Faceless Studio ("Faceless Studio," "we," "us," or
          "our"). By using the Services, you agree to these Terms and to our Privacy Policy.
        </p>
      </div>

      <div className="mt-10 space-y-10">
        {SECTIONS.map((s) => (
          <section key={s.n} data-testid={`terms-section-${s.n}`}>
            <h2 className="font-display font-bold text-xl text-white">
              <span className="text-[#FF6A5E]">{s.n}.</span> {s.title}
            </h2>
            {s.intro && <p className="mt-3 text-slate-400 leading-relaxed">{s.intro}</p>}
            {s.body && s.body.map((p, i) => <p key={`${s.n}-${i}`} className="mt-3 text-slate-400 leading-relaxed">{p}</p>)}
            {s.list && <Bullets items={s.list} />}
            {s.outro && <p className="mt-3 text-slate-400 leading-relaxed">{s.outro}</p>}
          </section>
        ))}

        <section data-testid="terms-section-20">
          <h2 className="font-display font-bold text-xl text-white">
            <span className="text-[#FF6A5E]">20.</span> Contact Us
          </h2>
          <p className="mt-3 text-slate-400 leading-relaxed">
            If you have questions about these Terms, contact us through the contact information provided on the
            Faceless Studio website.
          </p>
          <div className="mt-5 rounded-2xl border border-white/10 bg-[#11141E] p-5 space-y-2">
            <p className="font-semibold text-white">Faceless Studio</p>
            <p className="text-slate-400">
              Website:{" "}
              <a href="https://facelessstudio.org" className="text-[#FF6A5E] hover:underline" target="_blank" rel="noreferrer">
                facelessstudio.org
              </a>
            </p>
            <p className="text-slate-400">
              Contact:{" "}
              <a href="mailto:admin@facelessstudio.org" className="text-[#FF6A5E] hover:underline">
                admin@facelessstudio.org
              </a>
            </p>
          </div>
          <p className="mt-5 text-sm text-slate-500">
            See also our{" "}
            <Link to="/privacy" className="text-[#FF6A5E] hover:underline" data-testid="terms-privacy-link">
              Privacy Policy
            </Link>
            .
          </p>
        </section>
      </div>
    </div>
  );
}
